import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["entry", "swapButton"]
  static values = {
    eventId: Number,
    selectedEntryId: Number,
    selectedTeamId: Number
  }

  connect() {
    // Don't set to null - undefined is the proper default for optional Stimulus Number values
    // Setting to null causes NaN which breaks conditional checks
  }

  // Called when swap button is clicked
  selectEntry(event) {
    const entryElement = event.currentTarget.closest("[data-entry-id]")
    const entryId = parseInt(entryElement.dataset.entryId)
    const teamId = parseInt(entryElement.dataset.teamId)

    if (this.selectedEntryIdValue === entryId) {
      // Clicking the same entry cancels the swap
      this.cancel()
    } else {
      // Set this entry as the one to swap
      this.selectedEntryIdValue = entryId
      this.selectedTeamIdValue = teamId
      this.updateUI()
    }
  }

  // Called when clicking on an entry to swap with
  swapWith(event) {
    if (!this.selectedEntryIdValue) return

    // Prevent swap if clicking on a link or button (e.g., dropdown menu items)
    // This prevents accidental swaps when clicking "Make captain" or other actions
    const clickedElement = event.target
    if (clickedElement.tagName === 'A' || clickedElement.tagName === 'BUTTON' ||
        clickedElement.closest('a') || clickedElement.closest('button')) {
      return
    }

    const targetElement = event.currentTarget.closest("[data-entry-id]")
    const targetEntryId = parseInt(targetElement.dataset.entryId)
    const targetTeamId = parseInt(targetElement.dataset.teamId)

    // Can't swap with self or entries on the same team
    if (targetEntryId === this.selectedEntryIdValue || targetTeamId === this.selectedTeamIdValue) {
      return
    }

    // Perform the swap
    this.performSwap(this.selectedEntryIdValue, targetEntryId)
  }

  cancel() {
    delete this.selectedEntryIdValue
    delete this.selectedTeamIdValue
    this.updateUI()
  }

  updateUI() {
    this.entryTargets.forEach(entry => {
      const entryId = parseInt(entry.dataset.entryId)
      const teamId = parseInt(entry.dataset.teamId)
      const swapButton = entry.querySelector("[data-swap-entries-target='swapButton']")
      const gearButton = entry.querySelector("[data-dropdown-target='button']")

      // Remove all swap-related classes
      entry.classList.remove("swap-selected", "swap-swappable", "swap-disabled")
      if (swapButton) {
        swapButton.classList.remove("text-indigo-600", "dark:text-indigo-400", "pointer-events-none")
        swapButton.classList.add("text-gray-400")
      }
      if (gearButton) {
        gearButton.classList.remove("pointer-events-none")
      }

      if (this.selectedEntryIdValue === undefined) {
        // No entry selected, all entries are in normal state
        entry.style.cursor = "default"
      } else if (entryId === this.selectedEntryIdValue) {
        // This is the selected entry - keep swap button active for canceling
        entry.classList.add("swap-selected")
        if (swapButton) {
          swapButton.classList.remove("text-gray-400")
          swapButton.classList.add("text-indigo-600", "dark:text-indigo-400")
        }
        // Disable gear button on selected entry
        if (gearButton) {
          gearButton.classList.add("pointer-events-none")
        }
      } else if (teamId === this.selectedTeamIdValue) {
        // Same team - not swappable, disable all buttons
        entry.classList.add("swap-disabled")
        entry.style.cursor = "not-allowed"
        if (swapButton) {
          swapButton.classList.add("pointer-events-none")
        }
        if (gearButton) {
          gearButton.classList.add("pointer-events-none")
        }
      } else {
        // Different team - swappable, disable buttons so only the row is clickable
        entry.classList.add("swap-swappable")
        entry.style.cursor = "pointer"
        if (swapButton) {
          swapButton.classList.add("pointer-events-none")
        }
        if (gearButton) {
          gearButton.classList.add("pointer-events-none")
        }
      }
    })
  }

  async performSwap(entryId, otherEntryId) {
    const url = `/director/events/${this.eventIdValue}/entries/${entryId}/swap`
    const csrfToken = document.querySelector("[name='csrf-token']").content

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ other_entry_id: otherEntryId })
      })

      if (response.ok) {
        const turboStream = await response.text()
        // Cancel BEFORE rendering to clear state synchronously
        // This prevents race conditions where user clicks before state is cleared
        this.cancel()
        Turbo.renderStreamMessage(turboStream)
      } else {
        console.error("Swap failed:", response)
      }
    } catch (error) {
      console.error("Error performing swap:", error)
    }
  }
}
